<template>
  <div class="analyse-calls p-2">
    <LoadingSpinner v-if="loading" :title="loadingTitle" />

    <div v-else-if="error" class="alert alert-danger mb-0" role="alert">
      {{ error }}
    </div>

    <div v-else-if="data" class="analyse-calls__content">
      <div class="analyse-calls__header">
        <div class="analyse-calls__title">
          <span class="analyse-calls__badge">{{ recommandations.length }}</span>
          <h5 class="mb-0">Recommandations</h5>
        </div>
        <div class="analyse-calls__model text-muted">
          <span v-if="data.model">{{ data.model }}</span>
        </div>
      </div>

      <div class="analyse-calls__meta text-muted small mb-2">
        <span v-if="data.calls_count != null">
          <strong>{{ data.calls_count }}</strong> appel(s) analysé(s)
        </span>
        <span v-if="data.audios_attached != null" class="ml-2">
          · <strong>{{ data.audios_attached }}</strong> audio(s) attaché(s)
        </span>
        <span
          v-if="Array.isArray(data.audio_errors) && data.audio_errors.length"
          class="ml-2 text-danger"
        >
          · {{ data.audio_errors.length }} erreur(s) audio
        </span>
      </div>

      <p class="analyse-calls__note">
        Report-only. Rien n'est appliqué automatiquement. Chaque changement
        décrit ci-dessous est à faire à la main, à l'endroit indiqué.
      </p>

      <div v-if="syntheseDuLot.length" class="analyse-calls__synthese">
        <div class="analyse-calls__synthese-title">
          SYNTHÈSE DU LOT
          <span class="analyse-calls__synthese-count">
            ({{ data.calls_count || syntheseDuLot.length }} appels)
          </span>
        </div>
        <ul class="analyse-calls__synthese-list">
          <li v-for="(item, idx) in syntheseDuLot" :key="`synth-${idx}`">
            <span v-if="item.affected_calls_count != null">
              <strong>{{ item.affected_calls_count }}</strong>
              appel(s)
            </span>
            <span v-if="item.track_or_layer" class="text-muted">
              · {{ item.track_or_layer }}
            </span>
            <span v-if="item.constat"> — {{ item.constat }}</span>
          </li>
        </ul>
      </div>

      <div
        v-if="!recommandations.length"
        class="alert alert-info mb-0"
        role="alert"
      >
        Aucune recommandation pour cette sélection.
      </div>

      <div
        v-for="(reco, idx) in recommandations"
        :key="`reco-${idx}`"
        class="analyse-calls__reco"
      >
        <div class="analyse-calls__reco-head">
          <div class="analyse-calls__reco-tags">
            <span
              v-if="reco.couche"
              class="analyse-calls__tag"
              :class="layerClass(reco.couche)"
            >
              {{ reco.couche }}
            </span>
            <span
              v-if="reco.gravite"
              class="analyse-calls__severity"
              :class="severityClass(reco.gravite)"
            >
              {{ reco.gravite }}
            </span>
            <span class="analyse-calls__reco-title">
              {{ reco.symptome }}
            </span>
            <span
              v-if="reco.socle_warning"
              class="analyse-calls__warning"
              :title="reco.socle_warning"
            >
              {{ reco.socle_warning }}
            </span>
          </div>
          <div
            v-if="reco.confiance"
            class="analyse-calls__confidence text-muted"
          >
            Confiance : <strong>{{ reco.confiance }}</strong>
          </div>
        </div>

        <div v-if="reco.preuve" class="analyse-calls__proof">
          <span v-if="reco.preuve.call_id" class="analyse-calls__proof-call">
            {{ formatCallIds(reco.preuve.call_id) }}
          </span>
          <span
            v-if="
              Array.isArray(reco.preuve.timestamps) &&
              reco.preuve.timestamps.length
            "
            class="analyse-calls__proof-time"
          >
            , {{ reco.preuve.timestamps.join(' / ') }}
          </span>
          <span v-if="reco.preuve.citation" class="analyse-calls__proof-quote">
            · {{ reco.preuve.citation }}
          </span>
        </div>

        <div v-if="reco.diagnostic" class="analyse-calls__diagnostic">
          <strong>Diagnostic :</strong> {{ reco.diagnostic }}
        </div>

        <div v-if="reco.changement_exact" class="analyse-calls__change">
          <div class="analyse-calls__change-label">CHANGEMENT EXACT</div>
          <div class="analyse-calls__change-text">
            {{ reco.changement_exact }}
          </div>
        </div>

        <div
          v-if="
            reco.gain_estime ||
            reco.dependance ||
            reco.llm_latency_cost
          "
          class="analyse-calls__extras text-muted small"
        >
          <span v-if="reco.gain_estime">
            <strong>Gain estimé :</strong> {{ reco.gain_estime }}
          </span>
          <span v-if="reco.dependance && reco.dependance !== 'aucune'" class="ml-2">
            · <strong>Dépendance :</strong> {{ reco.dependance }}
          </span>
          <span v-if="reco.llm_latency_cost" class="ml-2">
            · <strong>Latence/coût LLM :</strong> {{ reco.llm_latency_cost }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { adminService } from '@/_services'

export default {
  name: 'AnalyseCalls',

  components: {
    LoadingSpinner,
  },

  props: {
    clientId: {
      type: [String, Number],
      required: true,
    },
    callIds: {
      type: Array,
      required: true,
      default: () => [],
    },
    autoLoad: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      loading: false,
      error: null,
      data: null,
    }
  },

  computed: {
    loadingTitle() {
      const total = Array.isArray(this.callIds) ? this.callIds.length : 0
      if (!total) return 'Analyse en cours…'
      return `Analyse de ${total} appel(s) en cours…`
    },
    syntheseDuLot() {
      const synth =
        this.data && this.data.analysis && this.data.analysis.synthese_du_lot
      return Array.isArray(synth) ? synth : []
    },
    recommandations() {
      const recos =
        this.data && this.data.analysis && this.data.analysis.recommandations
      return Array.isArray(recos) ? recos : []
    },
  },

  watch: {
    clientId() {
      if (this.autoLoad) this.fetchAnalyse()
    },
    callIds() {
      if (this.autoLoad) this.fetchAnalyse()
    },
  },

  mounted() {
    if (this.autoLoad) this.fetchAnalyse()
  },

  methods: {
    fetchAnalyse() {
      if (!this.clientId) return
      const normalizedIds = (this.callIds || [])
        .map((id) => {
          const n = parseInt(id, 10)
          return Number.isInteger(n) && n > 0 ? n : null
        })
        .filter((id) => id !== null)

      if (!normalizedIds.length) {
        this.data = null
        this.error = null
        return
      }

      this.loading = true
      this.error = null
      this.data = null

      adminService
        .analyseClientCalls(this.clientId, normalizedIds)
        .then((res) => {
          this.data = res || null
        })
        .catch((err) => {
          this.error =
            (err && (err.error || err.message)) ||
            'Une erreur est survenue lors de l\'analyse.'
        })
        .finally(() => {
          this.loading = false
        })
    },
    layerClass(couche) {
      const value = String(couche || '').toLowerCase()
      if (value.includes('config')) return 'analyse-calls__tag--config'
      if (value.includes('socle')) return 'analyse-calls__tag--socle'
      if (value.includes('partie') || value.includes('variable'))
        return 'analyse-calls__tag--variable'
      return 'analyse-calls__tag--default'
    },
    severityClass(gravite) {
      const value = String(gravite || '').toLowerCase()
      if (value.includes('critic')) return 'analyse-calls__severity--critical'
      if (value.includes('import')) return 'analyse-calls__severity--important'
      if (value.includes('optim')) return 'analyse-calls__severity--optim'
      return 'analyse-calls__severity--default'
    },
    formatCallIds(callIds) {
      if (!callIds) return ''
      if (Array.isArray(callIds)) return callIds.join(', ')
      return String(callIds)
    },
  },
}
</script>

<style scoped>
.analyse-calls {
  font-size: 0.92rem;
}

.analyse-calls__content {
  background-color: #fafbff;
  border: 1px solid #e7e9f3;
  border-radius: 8px;
  padding: 18px 20px;
}

.analyse-calls__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.analyse-calls__title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.analyse-calls__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background-color: #4a5fd4;
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
}

.analyse-calls__model {
  font-size: 0.85rem;
  font-style: italic;
}

.analyse-calls__note {
  background-color: #f5f6fb;
  border: 1px solid #e7e9f3;
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 14px;
  color: #555;
  font-size: 0.88rem;
}

.analyse-calls__synthese {
  background-color: #fff;
  border: 1px solid #e7e9f3;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
}

.analyse-calls__synthese-title {
  font-weight: 700;
  letter-spacing: 0.5px;
  font-size: 0.78rem;
  color: #2b2f44;
  margin-bottom: 8px;
}

.analyse-calls__synthese-count {
  font-weight: 500;
  color: #7a7f99;
}

.analyse-calls__synthese-list {
  margin: 0;
  padding-left: 18px;
  color: #3b3f55;
}

.analyse-calls__synthese-list li {
  margin-bottom: 4px;
}

.analyse-calls__reco {
  background-color: #fff;
  border: 1px solid #e7e9f3;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 12px;
}

.analyse-calls__reco-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}

.analyse-calls__reco-tags {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.analyse-calls__reco-title {
  font-weight: 600;
  color: #2b2f44;
}

.analyse-calls__tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.analyse-calls__tag--config {
  background-color: #ecebff;
  color: #5a47d8;
}

.analyse-calls__tag--socle {
  background-color: #fff3e0;
  color: #c97a18;
}

.analyse-calls__tag--variable {
  background-color: #ffe9f0;
  color: #c84a73;
}

.analyse-calls__tag--default {
  background-color: #eef0f7;
  color: #4a5068;
}

.analyse-calls__severity {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.analyse-calls__severity--critical {
  background-color: #fde4e4;
  color: #b3261e;
}

.analyse-calls__severity--important {
  background-color: #fff0d6;
  color: #a35a00;
}

.analyse-calls__severity--optim {
  background-color: #e1f3e6;
  color: #1f7a3a;
}

.analyse-calls__severity--default {
  background-color: #eef0f7;
  color: #4a5068;
}

.analyse-calls__warning {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.72rem;
  font-weight: 600;
  background-color: #fff7e0;
  color: #8a6500;
  border: 1px dashed #e3c463;
}

.analyse-calls__confidence {
  font-size: 0.82rem;
  white-space: nowrap;
}

.analyse-calls__proof {
  font-style: italic;
  color: #6a6f88;
  background-color: #fafbff;
  padding: 6px 10px;
  border-left: 3px solid #d6d9e8;
  margin: 8px 0;
  font-size: 0.86rem;
  word-break: break-all;
}

.analyse-calls__proof-call {
  font-weight: 600;
  color: #4a5068;
}

.analyse-calls__diagnostic {
  margin: 6px 0 10px;
  color: #3b3f55;
}

.analyse-calls__change {
  background-color: #f7f8fc;
  border: 1px solid #e7e9f3;
  border-radius: 4px;
  padding: 10px 12px;
  margin-top: 8px;
}

.analyse-calls__change-label {
  font-weight: 700;
  font-size: 0.72rem;
  letter-spacing: 0.5px;
  color: #4a5fd4;
  margin-bottom: 4px;
}

.analyse-calls__change-text {
  color: #2b2f44;
  white-space: pre-wrap;
}

.analyse-calls__extras {
  margin-top: 8px;
}
</style>
